import {useCallback, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {gql, useMutation} from '@apollo/client';
import styles from './verify.module.scss';
import {useAuthToken} from '../../hooks/useAuthToken';
import Checkbox from '../../components/checkbox/Checkbox';
import Button from 'src/components/button/Button';
import NewAuthPageLayout from '@src/components/auth-page-layout/NewAuthPageLayout';
import AuthCode, { AuthCodeRef } from 'react-auth-code-input';
import './customStyle.css';
import ArrowIcon from 'src/assets/icons/curvearrow.svg?react';
import moment from 'moment';
import { logger } from '../../utils/logger';

type Props = {};

const VerifyMutation = gql`
  mutation verifyEmailCode($input: VerifyCodeInput!) {
    verifyEmailCode(input: $input)
  }
`

const ResendMutation = gql`
  mutation resendEmailCode($input: EmailInput!) {
    resendEmailCode(input: $input)
  }
`

function AdminVerify(props: Props) {
  const [mfa_code, setMFACode] = useState('');
  const [authError, setAuthError] = useState('');
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push, location } = useHistory();
  const countdownDuration = 60 * 10; // 10 minutes in seconds
  const [remainingSeconds, setRemainingSeconds] = useState(countdownDuration);
  const [disableMFA, setDisableMFA] = useState(false);
  const [ableToResend, setAbleToResend] = useState(true);
  let resendTimer = useRef<NodeJS.Timeout>();
  const AuthInputRef = useRef<AuthCodeRef>(null);

  const [verify, {loading, error}] = useMutation<{ verifyEmailCode: string }>(VerifyMutation, {
    onCompleted: (data) => {
      resendTimer.current && clearInterval(resendTimer.current)
      setAuthToken(data.verifyEmailCode)
      push('/');
    },
    onError: (error) => {
      logger.debug(`[Error]`, error);
    },
  });
  const [resend, { loading: resending}] = useMutation(ResendMutation)

  useEffect(() => {
    const timerInterval = setInterval(() => {
        setRemainingSeconds(prevSeconds => prevSeconds - 1);
    }, 1000);

    if (remainingSeconds <= 0) {
      clearInterval(timerInterval);
    }

    return () => clearInterval(timerInterval);
  }, [remainingSeconds]);

  useEffect(() => {
    if (error?.message) {
      setAuthError(error.message)
    }
  }, [error])

  const handleChange = useCallback((value) => {
    setMFACode(value);
  }, [])

  const handleDisableMFA = () => {
    setDisableMFA(!disableMFA);
  }

  const state: any = location.state;

  if (!state) {
    push('/');
    return null;
  } else {
    const handleSubmit = async () => {
      removeAuthToken();
      try {
        await verify({
          variables: {
            input: {
              code: mfa_code,
              disable: disableMFA,
              email: state.email,
              clientId: localStorage.getItem('clientId')
            }
          }
        });
      } catch (e) {
        logger.info(e)
      }
    }

    const resendCode = async () => {
      setAbleToResend(false)
      const sent = await resend({ variables: { input: { email: state.email } } })
      if (sent.data?.resendEmailCode) {
        setAbleToResend(true)
        setMFACode('')
        setAuthError('')
        setRemainingSeconds(countdownDuration)
        AuthInputRef.current?.clear()
      }
    }

    const handleLogin = () => {
      push('/login');
    }

    const emailMask = `${state.email.charAt(0)}...@${state.email.split('@')[1]}`
    const formattedTime = moment.utc(remainingSeconds * 1000).format('mm:ss')

    return (
      <NewAuthPageLayout onSubmit={handleSubmit} loading={loading} title={<div>Email Authentication</div>} buttonTitle={'Verify'}>
        {authError && <>
          <div className={styles.noteBlock}>
            <div className={styles.noteBlock_label} dangerouslySetInnerHTML={{ __html: authError }} />
            {authError.startsWith('Your authentication code has expired.') && <Button disabled={resending} className={styles.resendButton} label={'Resend code'} isLoading={resending} onClick={resendCode} />}
          </div>
        </>}
        <div className={styles.ssoNotice}>
          <span>Sign In using Single Sign-On to avoid this step!</span>
          <Button label="Click to Sign-In with SSO" size='small' className={styles.loginButton} onClick={handleLogin}/>
        </div>
        <div className={styles.verifyEmail}>
          A verification code was sent to <span className={styles.emailMask}>{emailMask}</span><br/>
          Check your email and enter the code below.
        </div>
        <AuthCode onChange={handleChange} inputClassName={"codeInput"} containerClassName="authContainer" length={6} ref={AuthInputRef} />
        {remainingSeconds > 0 && (
          <div className={styles.expireWrapper}>
            <div className={styles.error}>
              {`Expires In ${formattedTime}`}
            </div>
          </div>
        )}
        <div className={styles.checkboxWrapper}>
          <Checkbox id={'disableMFA'} label={'Disable Two-Factor-Authentication on this computer for 30 days.'} checked={disableMFA} onChange={handleDisableMFA} className={styles.flexStart}/>
        </div>
        <div className={styles.arrowDiv}>
            <span className={styles.arrowText}>Need help logging in?<br/>Click to Chat with us!</span>
            <ArrowIcon width="40" height="200" />
          </div>
        <Button disabled={loading || mfa_code === '' || !ableToResend} className={styles.verifyButton} label={'Verify'} isLoading={loading} onClick={handleSubmit} />
        <div className={styles.back}>
          Didn't get an email? &nbsp;<div className={styles.backLink} onClick={resendCode}>Resend code</div>
        </div>
      </NewAuthPageLayout>
    );
  }
}

export default AdminVerify;
